import template from './uc-milestone-manager.html.twig';
import './uc-milestone-manager.scss';

const { Mixin } = Shopware;
const { Criteria } = Shopware.Data;

Shopware.Component.register('uc-milestone-manager', {
    template,

    inject: ['repositoryFactory'],

    mixins: [
        Mixin.getByName('notification'),
    ],

    props: {
        salesChannelId: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            milestones: null,
            isLoading: false,
            // Distinguishes "no milestones created yet" from "the list
            // failed to load" — both leave `milestones` empty/null, but
            // showing the plain empty state for a real load failure would
            // wrongly suggest to the merchant that their milestones vanished.
            // See loadMilestones().
            loadError: false,
            showModal: false,
            isNewMilestone: false,
            isSaving: false,
            isDeleting: false,
            deleteId: null,
            showDeleteConfirm: false,

            // Flat form — Vue 3 Proxy makes direct property mutations reactive
            form: {
                id:                    null,
                salesChannelId:        null,
                position:              1,
                threshold:             50,
                rewardType:            'free_shipping',
                rewardValue:           null,
                promotionId:           null,
                iconUrl:               null,
                labelBefore:           '',
                labelAfter:            '',
                animationType:         'none',
                active:                true,
            },

            // UI-only toggle — not persisted. Derived from whether promotionId
            // is set when editing; drives which of rewardValue/promotionId is shown.
            rewardSource: 'custom',

            // Full promotion entity (with salesChannels/discounts loaded) for
            // whichever promotion is currently selected in the form — kept
            // separate from `milestones` so we can show a live eligibility
            // check while the merchant is still picking, before saving.
            selectedPromotionDetail: null,
            isLoadingPromotionDetail: false,

            // Cart-amount value detected on the selected promotion's own
            // "Conditions" (cartCartAmount rule), if any — see
            // detectCartAmountThreshold(). Null when none was found.
            detectedThreshold: null,
        };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('sellops_uc_progress_milestone');
        },

        promotionRepository() {
            return this.repositoryFactory.create('promotion');
        },


        rewardTypeOptions() {
            return [
                { value: 'free_shipping',    label: this.$tc('sellops-ultimate-cart.milestones.rewardType.free_shipping') },
                { value: 'discount_percent', label: this.$tc('sellops-ultimate-cart.milestones.rewardType.discount_percent') },
                { value: 'discount_fixed',   label: this.$tc('sellops-ultimate-cart.milestones.rewardType.discount_fixed') },
            ];
        },

        showRewardValue() {
            return ['discount_percent', 'discount_fixed'].includes(this.form.rewardType);
        },


        rewardValueLabel() {
            return this.form.rewardType === 'discount_percent'
                ? this.$tc('sellops-ultimate-cart.milestones.rewardValuePercent')
                : this.$tc('sellops-ultimate-cart.milestones.rewardValueFixed');
        },

        rewardSourceOptions() {
            return [
                { value: 'custom',    label: this.$tc('sellops-ultimate-cart.milestones.rewardSource.custom') },
                { value: 'promotion', label: this.$tc('sellops-ultimate-cart.milestones.rewardSource.promotion') },
            ];
        },

        // Celebration animation played once, client-side, the moment a
        // shopper crosses this milestone — see
        // docs/celebration-animations-architecture.md. Purely cosmetic;
        // has no effect on the reward itself.
        animationTypeOptions() {
            return [
                { value: 'none',      label: this.$tc('sellops-ultimate-cart.milestones.animationType.none') },
                { value: 'confetti',  label: this.$tc('sellops-ultimate-cart.milestones.animationType.confetti') },
                { value: 'fireworks', label: this.$tc('sellops-ultimate-cart.milestones.animationType.fireworks') },
                { value: 'gift',      label: this.$tc('sellops-ultimate-cart.milestones.animationType.gift') },
                { value: 'premium',   label: this.$tc('sellops-ultimate-cart.milestones.animationType.premium') },
            ];
        },

        // Automatic (codeless) and Fixed-code (one shared code) promotions
        // both work here — Shopware applies automatic ones on its own, and a
        // Fixed code gets redeemed for the customer once the threshold is
        // crossed (see MilestoneFixedCodePromotionSync on the backend).
        // Individual (per-customer) codes are excluded: nothing can pick
        // "the" code to redeem on the customer's behalf when every customer
        // needs a different one.
        promotionCriteria() {
            const criteria = new Criteria(1, 25);
            criteria.addFilter(Criteria.equals('active', true));
            criteria.addFilter(Criteria.multi('OR', [
                Criteria.equals('useCodes', false),
                Criteria.multi('AND', [
                    Criteria.equals('useCodes', true),
                    Criteria.equals('useIndividualCodes', false),
                ]),
            ]));
            return criteria;
        },

        sortedMilestones() {
            if (!this.milestones) return [];
            return [...this.milestones].sort((a, b) => a.position - b.position);
        },

        // Live eligibility status for whatever promotion is currently picked
        // in the form (see fetchPromotionDetail / watch: form.promotionId).
        selectedPromotionStatus() {
            if (!this.form.promotionId) return null;
            if (this.isLoadingPromotionDetail) return null;
            return this.promotionStatus(this.selectedPromotionDetail);
        },

        // Combination conflicts for whatever promotion is currently picked
        // in the form, checked against every OTHER milestone's promotion —
        // see promotionConflicts() for why this matters.
        selectedPromotionConflicts() {
            if (!this.form.promotionId || this.isLoadingPromotionDetail) return [];
            return this.promotionConflicts(this.selectedPromotionDetail, this.form.id);
        },
    },

    watch: {
        salesChannelId() {
            this.loadMilestones();
        },

        // Clear fields that don't belong to the newly selected reward type
        'form.rewardType'(newType) {
            if (!['discount_percent', 'discount_fixed'].includes(newType)) {
                this.form.rewardValue = null;
                this.form.promotionId = null;
                this.rewardSource = 'custom';
            }
        },

        // Reward source is UI-only — clear whichever field the merchant
        // just switched away from so a stale value can't get saved.
        rewardSource(newSource) {
            if (newSource === 'custom') {
                this.form.promotionId = null;
                this.selectedPromotionDetail = null;
                this.detectedThreshold = null;
            } else {
                this.form.rewardValue = null;
            }
        },
    },

    created() {
        this.loadMilestones();
    },

    methods: {
        async loadMilestones() {
            this.isLoading = true;
            this.loadError = false;
            try {
                const criteria = new Criteria();
                criteria.addSorting(Criteria.sort('position', 'ASC'));
                // 'promotion.salesChannels' also implicitly loads 'promotion'
                criteria.addAssociation('promotion.salesChannels');
                this.milestones = await this.repository.search(criteria, Shopware.Context.api);
            } catch (e) {
                console.error('[UltimateCart] Failed to load milestones:', e);
                this.milestones = null;
                this.loadError = true;
            } finally {
                this.isLoading = false;
            }
        },

        openCreate() {
            this.form = {
                id:                    null,
                salesChannelId:        this.salesChannelId ?? null,
                position:              (this.milestones?.total ?? 0) + 1,
                threshold:             50,
                rewardType:            'free_shipping',
                rewardValue:           null,
                promotionId:           null,
                iconUrl:               null,
                labelBefore:           'Add {remaining} more for free shipping',
                labelAfter:            '🎉 Free shipping unlocked!',
                animationType:         'none',
                active:                true,
            };
            this.rewardSource = 'custom';
            this.selectedPromotionDetail = null;
            this.detectedThreshold = null;
            this.isNewMilestone = true;
            this.showModal = true;
        },

        openEdit(milestone) {
            this.form = {
                id:                    milestone.id,
                salesChannelId:        milestone.salesChannelId ?? null,
                position:              milestone.position,
                threshold:             milestone.threshold,
                rewardType:            milestone.rewardType,
                rewardValue:           milestone.rewardValue,
                promotionId:           milestone.promotionId ?? null,
                iconUrl:               milestone.iconUrl,
                labelBefore:           milestone.labelBefore,
                labelAfter:            milestone.labelAfter,
                animationType:         milestone.animationType ?? 'none',
                active:                milestone.active,
            };
            this.rewardSource = milestone.promotionId ? 'promotion' : 'custom';
            this.isNewMilestone = false;
            this.showModal = true;

            // Load the promotion for the live eligibility check, but never
            // auto-fill the threshold here — this milestone was already
            // configured (possibly deliberately, to differ from the
            // promotion's own condition), so silently overwriting a saved
            // value while the merchant is just reviewing/editing would be a
            // surprise, not a convenience. Auto-fill only happens when the
            // merchant actively PICKS a promotion — see onPromotionSelected().
            if (milestone.promotionId) {
                this.fetchPromotionDetail(milestone.promotionId, false);
            }
        },

        async saveMilestone() {
            this.isSaving = true;
            try {
                let entity;

                if (this.isNewMilestone) {
                    // Create: let repository generate the UUID
                    entity = this.repository.create(Shopware.Context.api);
                } else {
                    // Update: load existing entity so Shopware tracks it correctly
                    entity = await this.repository.get(this.form.id, Shopware.Context.api);
                }

                // Apply form values — skip id (entity owns its own id)
                const { id, ...formData } = this.form;
                Object.assign(entity, formData);

                await this.repository.save(entity, Shopware.Context.api);

                // Close the modal before the growl fires — both teleport to
                // <body>; patching them in one flush crashes Vue (insertBefore)
                this.showModal = false;
                await this.$nextTick();
                await this.loadMilestones();

                this.createNotificationSuccess({
                    message: this.$tc('sellops-ultimate-cart.milestones.saveSuccess'),
                });
            } catch (e) {
                console.error('[UltimateCart] Failed to save milestone:', e);
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.milestones.saveError'),
                });
            } finally {
                this.isSaving = false;
            }
        },

        cancelModal() {
            this.showModal = false;
        },

        confirmDelete(id) {
            this.deleteId = id;
            this.showDeleteConfirm = true;
        },

        cancelDelete() {
            this.deleteId = null;
            this.showDeleteConfirm = false;
        },

        async executeDelete() {
            if (!this.deleteId) return;
            this.isDeleting = true;
            const idToDelete = this.deleteId;
            try {
                await this.repository.delete(idToDelete, Shopware.Context.api);

                // Close the confirm modal before the growl fires (teleport clash)
                this.isDeleting = false;
                this.deleteId = null;
                this.showDeleteConfirm = false;
                await this.$nextTick();
                await this.loadMilestones();

                this.createNotificationSuccess({
                    message: this.$tc('sellops-ultimate-cart.milestones.deleteSuccess'),
                });
            } catch (e) {
                console.error('[UltimateCart] Failed to delete milestone:', e);
                this.isDeleting = false;
                this.deleteId = null;
                this.showDeleteConfirm = false;
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.milestones.deleteError'),
                });
            }
        },

        rewardLabel(milestone) {
            // Promotion-backed discount — show the promotion's own name so the
            // merchant can see which Shopware Promotion is doing the work.
            // 🔑 marks a Fixed-code promotion the plugin auto-redeems; 🏷️
            // marks an automatic one Shopware applies entirely on its own.
            if (milestone.promotionId) {
                const name = milestone.promotion?.translated?.name ?? milestone.promotion?.name;
                if (!name) {
                    return this.$tc('sellops-ultimate-cart.milestones.promotionMissing');
                }
                const icon = this.isFixedCodePromotion(milestone.promotion) ? '🔑' : '🏷️';
                return `${icon} ${name}`;
            }

            switch (milestone.rewardType) {
                case 'free_shipping':    return this.$tc('sellops-ultimate-cart.milestones.rewardType.free_shipping');
                case 'discount_percent': return `${milestone.rewardValue ?? 0}% off`;
                case 'discount_fixed':   return `€${milestone.rewardValue ?? 0} off`;
                default:                 return milestone.rewardType;
            }
        },

        // Bound to the promotion picker's @update:value — the ONE place
        // auto-fill is allowed to kick in, since it's the moment the merchant
        // actively chose this promotion (as opposed to openEdit() loading an
        // already-configured milestone for review).
        onPromotionSelected(promotionId) {
            this.form.promotionId = promotionId;
            this.fetchPromotionDetail(promotionId, true);
        },

        // Loads the full promotion (with the associations needed for the
        // eligibility check + threshold detection). Called whenever the
        // merchant picks a promotion, and once when opening an existing
        // promotion-backed milestone for editing.
        //
        // @param {boolean} attemptThresholdAutoFill When true and a simple
        //   "cart amount ≥ X" condition is found on the promotion's own
        //   Conditions, form.threshold is overwritten with that amount and
        //   the merchant is notified — see detectCartAmountThreshold().
        async fetchPromotionDetail(promotionId, attemptThresholdAutoFill = false) {
            if (!promotionId) {
                this.selectedPromotionDetail = null;
                this.detectedThreshold = null;
                return;
            }

            this.isLoadingPromotionDetail = true;
            try {
                this.selectedPromotionDetail = await this.promotionRepository.get(
                    promotionId,
                    Shopware.Context.api,
                    this.promotionDetailCriteria(),
                );

                this.detectedThreshold = this.detectCartAmountThreshold(this.selectedPromotionDetail);

                if (attemptThresholdAutoFill && this.detectedThreshold !== null) {
                    this.form.threshold = this.detectedThreshold;
                    this.createNotificationInfo({
                        message: this.$tc('sellops-ultimate-cart.milestones.thresholdAutoFilled'),
                    });
                }
            } catch (e) {
                console.error('[UltimateCart] Failed to load promotion detail:', e);
                // Repository .get() 404s when the promotion was deleted — that's
                // itself a valid, reportable status ("not-found"), not a hard error.
                this.selectedPromotionDetail = null;
                this.detectedThreshold = null;
            } finally {
                this.isLoadingPromotionDetail = false;
            }
        },

        /**
         * Looks for a single, unambiguous cart-amount condition anywhere in
         * the promotion's own Conditions (cartRules). The Shopware rule
         * builder UI always wraps even a single condition in orContainer/
         * andContainer nodes (confirmed against real API payloads — e.g. a
         * plain "Subtotal of all items >= 1000" condition is stored as
         * orContainer -> andContainer -> cartGoodsPrice), so this walks the
         * whole flat conditions list rather than only inspecting root-level
         * nodes.
         *
         * Two Shopware core rule types represent a cart-wide amount
         * threshold merchants would mean by "cart total": `cartCartAmount`
         * ("Grand total" — the cart's final total, incl. shipping/tax
         * adjustments) and `cartGoodsPrice` ("Subtotal of all items" — goods
         * price before shipping, the one most "spend over X" promotions
         * actually use in practice). Both are recognized here; container
         * types (`andContainer`/`orContainer`) and unrelated leaf types
         * (customer group, weekday, etc.) are ignored since they carry no
         * `amount` value.
         *
         * Only auto-fills when EXACTLY ONE such amount-bearing condition
         * exists across the whole tree — if a rule OR's together two
         * different thresholds (e.g. "total >= 1000 OR total >= 2000"),
         * guessing "the" number out of a compound rule would be unreliable,
         * so it's left for the merchant to read and set manually.
         *
         * @return {number|null}
         */
        detectCartAmountThreshold(promotion) {
            if (!promotion || !promotion.cartRules || !promotion.cartRules.length) {
                return null;
            }

            const AMOUNT_CONDITION_TYPES = ['cartCartAmount', 'cartGoodsPrice'];

            for (const rule of promotion.cartRules) {
                const conditions = rule.conditions ?? [];
                const amountLeaves = conditions.filter((c) => AMOUNT_CONDITION_TYPES.includes(c.type));

                if (amountLeaves.length !== 1) {
                    continue; // none found, or ambiguous (more than one amount condition)
                }

                const [leaf] = amountLeaves;
                if (leaf.value && typeof leaf.value.amount === 'number') {
                    return leaf.value.amount;
                }
            }

            return null;
        },

        promotionDetailCriteria() {
            const criteria = new Criteria();
            criteria.addAssociation('salesChannels');
            // Needed for detectCartAmountThreshold() — the promotion's own
            // "Conditions" tab (cart rules), each with its condition tree.
            criteria.addAssociation('cartRules.conditions');
            return criteria;
        },

        /**
         * Same eligibility check the storefront/backend use (active, within
         * its valid date range, assigned to at least one sales channel, and a
         * SUPPORTED code type), evaluated here so the merchant gets the same
         * answer while editing instead of only discovering it's broken by
         * checking the storefront. Returns null when everything is fine,
         * otherwise a status key used to look up the specific reason text.
         *
         * Automatic (no code) AND Fixed-code (one shared code) promotions are
         * both supported — see isFixedCodePromotion(). Only Individual
         * (per-customer) codes are unsupported: nothing can pick "the" code
         * to redeem on the customer's behalf when every customer needs a
         * different one.
         */
        promotionStatus(promotion) {
            if (!promotion) return 'not-found';
            if (!promotion.active) return 'inactive';
            if (promotion.useCodes && promotion.useIndividualCodes) return 'individual-code';

            const now = new Date();
            if (promotion.validFrom && new Date(promotion.validFrom) > now) return 'not-started';
            if (promotion.validUntil && new Date(promotion.validUntil) < now) return 'expired';

            if (promotion.salesChannels && promotion.salesChannels.length === 0) return 'no-sales-channel';

            return null;
        },

        // Fixed-code promotions apply differently from automatic ones — the
        // plugin redeems the shared code for the customer once the threshold
        // is crossed, instead of Shopware applying it on its own. Used purely
        // to pick which "everything's fine" message to show (see twig).
        isFixedCodePromotion(promotion) {
            return !!(promotion && promotion.useCodes && !promotion.useIndividualCodes);
        },

        promotionStatusLabel(status) {
            if (!status) return null;
            return this.$tc(`sellops-ultimate-cart.milestones.promotionStatus.${status}`);
        },

        // For the milestone table — only meaningful for saved milestones whose
        // `promotion` association was loaded via loadMilestones().
        milestonePromotionStatus(milestone) {
            if (!milestone.promotionId) return null;
            return this.promotionStatus(milestone.promotion);
        },

        /**
         * Cross-checks `promotion` against every OTHER milestone's linked
         * promotion for a mutual "won't combine" relationship — Shopware's
         * per-promotion `exclusionIds` ("Do not combine with" in Marketing →
         * Promotions) plus the broader `preventCombination` ("Prevent
         * combination with other promotions") toggle, checked from either
         * side since either promotion can be the one declaring the
         * exclusion.
         *
         * Why this matters for milestones specifically: every milestone's
         * promotion condition is a "cart total >= X" threshold, so once the
         * cart passes the HIGHER of any two milestones' thresholds, BOTH
         * promotions are simultaneously eligible — always, permanently, not
         * just as a one-off edge case. If those two promotions also happen
         * to exclude each other, Shopware keeps only the higher-Priority one
         * and silently drops the rest (no error, no cart notice — the exact
         * "why isn't this showing up" surprise the other status checks on
         * this screen already exist to catch). This surfaces that same class
         * of problem before it's a live customer-facing surprise.
         *
         * @param {object|null} promotion the promotion being checked
         * @param {string|null} ownMilestoneId the milestone this promotion
         *   belongs to — excluded from the comparison so a milestone is
         *   never flagged as conflicting with itself; null when creating a
         *   brand-new milestone (nothing to exclude yet).
         * @return {Array<{milestone: object, promotion: object, priority: number, otherPriority: number, outcome: 'wins'|'loses'|'tie'}>}
         */
        promotionConflicts(promotion, ownMilestoneId) {
            if (!promotion || !this.milestones) return [];

            const conflicts = [];

            for (const other of this.milestones) {
                if (other.id === ownMilestoneId) continue;
                if (!other.promotionId || !other.promotion) continue;
                if (other.promotion.id === promotion.id) continue; // same promotion — not a conflict

                const mutuallyExcluded =
                    (promotion.exclusionIds ?? []).includes(other.promotion.id) ||
                    (other.promotion.exclusionIds ?? []).includes(promotion.id) ||
                    !!promotion.preventCombination ||
                    !!other.promotion.preventCombination;

                if (!mutuallyExcluded) continue;

                let outcome = 'tie';
                if (promotion.priority > other.promotion.priority) {
                    outcome = 'wins';
                } else if (promotion.priority < other.promotion.priority) {
                    outcome = 'loses';
                }

                conflicts.push({
                    milestone: other,
                    promotion: other.promotion,
                    priority: promotion.priority,
                    otherPriority: other.promotion.priority,
                    outcome,
                });
            }

            return conflicts;
        },

        // For the milestone table — same conflict check as the form, run
        // against a saved milestone's own loaded promotion.
        milestonePromotionConflicts(milestone) {
            if (!milestone.promotionId || !milestone.promotion) return [];
            return this.promotionConflicts(milestone.promotion, milestone.id);
        },

        // Builds the merchant-facing sentence for one conflict entry. Same
        // wording regardless of whether it came from exclusionIds or the
        // broader preventCombination flag — the admin can check the
        // promotion's own Combination settings for the specifics once
        // they're flagged here.
        conflictMessage(conflict) {
            const key = `sellops-ultimate-cart.milestones.conflict${conflict.outcome.charAt(0).toUpperCase()}${conflict.outcome.slice(1)}`;
            return this.$tc(key, 0, {
                otherName: conflict.promotion.translated?.name ?? conflict.promotion.name ?? '',
                otherPosition: conflict.milestone.position,
                priority: conflict.priority,
                otherPriority: conflict.otherPriority,
            });
        },
    },
});
